import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  normalizeTradeValues,
  formatCurrency,
} from "../utils";
import API_BASE_URL from "../apiConfig";

const getMonthDays = (year, month) => {
  // month: 0-based
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfWeek = (year, month) => {
  // 0 = Sunday, 1 = Monday, ...
  return new Date(year, month, 1).getDay();
};

const Dashboard = () => {
  const navigate = useNavigate();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [trades, setTrades] = useState([]);
  const [viewMode, setViewMode] = useState("monthly"); // "monthly" or "yearly"

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/trades-grouped`);
        setTrades(response.data.map(normalizeTradeValues));
      } catch (error) {
        console.error('Error fetching trades:', error);
      }
    };
    fetchTrades();
  }, []);

  // Exclude open swings from P&L calculations
  const tradesForPnL = useMemo(
    () => trades.filter(t => t.total_sold !== 0 && t.date_closed),
    [trades]
  );

  // Group trades by date_closed (or fallback to date_opened)
  const tradesByDate = useMemo(() => {
    const groups = {};
    tradesForPnL.forEach(trade => {
      // Normalize date strings to YYYY-MM-DD format
      const dateStr = trade.date_closed || trade.date_opened;
      if (!dateStr) return;

      // Parse the date and normalize to YYYY-MM-DD format
      const [year, month, day] = dateStr.split("-");
      if (!year || !month || !day) return;

      // Construct normalized key with zero-padded month and day
      const normalizedKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      if (!groups[normalizedKey]) groups[normalizedKey] = [];
      groups[normalizedKey].push(trade);
    });
    return groups;
  }, [tradesForPnL]);

  // Calculate P&L for each day in the current month
  const calendarData = useMemo(() => {
    const daysInMonth = getMonthDays(year, month);
    const data = [];
    for (let day = 1; day <= daysInMonth; day++) {
      // Construct date key directly in YYYY-MM-DD format to match tradesByDate
      // month is 0-based, so add 1 for display
      const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const dayTrades = tradesByDate[dateKey] || [];
      let totalBuy = 0, totalSell = 0, totalFees = 0;
      dayTrades.forEach(trade => {
        totalBuy += Number(trade.total_buy_price) || 0;
        totalSell += Number(trade.total_sell_price) || 0;
        totalFees += (Number(trade.buy_fees) || 0) + (Number(trade.sell_fees) || 0);
      });
      const hasTrades = dayTrades.length > 0;
      let pnl = hasTrades ? totalSell - totalBuy - totalFees : null;
      // Show negative P&L as-is (do not force to 0)
      const pnlPercent = hasTrades && totalBuy > 0 && pnl !== null
        ? ((pnl / totalBuy) * 100).toFixed(2)
        : null;

      // Payout, Re-invest, Port calculation (only for pnl >= 1, else all 0)
      let payout = 0, reinvest = 0, port = 0;
      if (hasTrades && pnl !== null && pnl >= 1) {
        const payoutBase = pnl * 0.5;
        payout = Math.floor(payoutBase / 5) * 5
        if (payout < 0) payout = 0;
        const reinvestBase = (pnl - payout) * 0.5;
        reinvest = Math.ceil(reinvestBase / 10) * 10;
        if (reinvest < 0) reinvest = 0;
        port = pnl - payout - reinvest;
        if (port < 0) port = 0;
      }
      // For pnl < 1 (including negative), payout/reinvest/port are 0
      if (!hasTrades || pnl === null || pnl < 1) {
        payout = 0; reinvest = 0; port = 0;
      }
      data.push({
        date: dateKey,
        day,
        pnl,
        pnlPercent,
        hasTrades,
        payout,
        reinvest,
        port,
      });
    }
    return data;
  }, [month, year, tradesByDate]);

  // Calculate summary for the month
  const monthSummary = useMemo(() => {
    let totalTrades = 0;
    let totalBuy = 0, totalSell = 0, totalFees = 0;
    let totalPayout = 0, totalReinvest = 0, totalPort = 0;
    let tradeDays = 0;
    calendarData.forEach(day => {
      if (day.hasTrades) {
        tradeDays++;
        const dayTrades = tradesByDate[day.date] || [];
        totalTrades += dayTrades.length;
        dayTrades.forEach(trade => {
          totalBuy += Number(trade.total_buy_price) || 0;
          totalSell += Number(trade.total_sell_price) || 0;
          totalFees += (Number(trade.buy_fees) || 0) + (Number(trade.sell_fees) || 0);
        });
        totalPayout += day.payout || 0;
        totalReinvest += day.reinvest || 0;
        totalPort += day.port || 0;
      }
    });
    const pnl = totalSell - totalBuy - totalFees;
    const pnlPercent = totalBuy > 0 ? ((pnl / totalBuy) * 100).toFixed(2) : null;
    return {
      totalTrades,
      tradeDays,
      pnl,
      pnlPercent,
      totalPayout,
      totalReinvest,
      totalPort,
    };
  }, [calendarData, tradesByDate]);

  // Calculate win/loss summary for the month
  const winLossSummary = useMemo(() => {
    let wins = 0, losses = 0;
    let totalTrades = 0;
    calendarData.forEach(day => {
      if (day.hasTrades && day.pnl !== null) {
        const dayTrades = tradesByDate[day.date] || [];
        totalTrades += dayTrades.length;
        if (day.pnl > 0) wins++;
        else if (day.pnl < 0) losses++;
      }
    });
    const totalTradingDays = wins + losses;
    const winPercent = totalTradingDays > 0 ? ((wins / totalTradingDays) * 100).toFixed(2) : "0.00";
    const lossPercent = totalTradingDays > 0 ? ((losses / totalTradingDays) * 100).toFixed(2) : "0.00";
    return {
      totalTrades,
      wins,
      losses,
      winPercent,
      lossPercent,
    };
  }, [calendarData, tradesByDate]);

  // Calculate yearly data for all 12 months
  const yearlyData = useMemo(() => {
    const monthsData = [];
    for (let m = 0; m < 12; m++) {
      const daysInMonth = getMonthDays(year, m);
      let totalTrades = 0;
      let totalBuy = 0, totalSell = 0, totalFees = 0;
      let wins = 0, losses = 0;
      let totalPayout = 0, totalReinvest = 0, totalPort = 0;

      for (let day = 1; day <= daysInMonth; day++) {
        const dateKey = `${year}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const dayTrades = tradesByDate[dateKey] || [];

        if (dayTrades.length > 0) {
          let dayBuy = 0, daySell = 0, dayFees = 0;
          totalTrades += dayTrades.length;
          dayTrades.forEach(trade => {
            dayBuy += Number(trade.total_buy_price) || 0;
            daySell += Number(trade.total_sell_price) || 0;
            dayFees += (Number(trade.buy_fees) || 0) + (Number(trade.sell_fees) || 0);
          });
          totalBuy += dayBuy;
          totalSell += daySell;
          totalFees += dayFees;

          const dayPnl = daySell - dayBuy - dayFees;
          if (dayPnl > 0) wins++;
          else if (dayPnl < 0) losses++;

          // Calculate payout, reinvest, port for the day
          if (dayPnl >= 1) {
            const payoutBase = dayPnl * 0.5;
            const payout = Math.floor(payoutBase / 5) * 5;
            const reinvestBase = (dayPnl - payout) * 0.5;
            const reinvest = Math.ceil(reinvestBase / 10) * 10;
            const port = dayPnl - payout - reinvest;
            totalPayout += payout > 0 ? payout : 0;
            totalReinvest += reinvest > 0 ? reinvest : 0;
            totalPort += port > 0 ? port : 0;
          }
        }
      }

      const pnl = totalSell - totalBuy - totalFees;
      const pnlPercent = totalBuy > 0 ? ((pnl / totalBuy) * 100).toFixed(2) : null;
      const totalTradingDays = wins + losses;
      const winPercent = totalTradingDays > 0 ? ((wins / totalTradingDays) * 100).toFixed(2) : "0.00";
      const lossPercent = totalTradingDays > 0 ? ((losses / totalTradingDays) * 100).toFixed(2) : "0.00";

      monthsData.push({
        month: m,
        totalTrades,
        tradeDays: totalTradingDays,
        pnl,
        pnlPercent,
        wins,
        losses,
        winPercent,
        lossPercent,
        totalPayout,
        totalReinvest,
        totalPort,
      });
    }
    return monthsData;
  }, [year, tradesByDate]);

  // Calculate summary for the entire year
  const yearlySummary = useMemo(() => {
    const summary = yearlyData.reduce((acc, monthData) => ({
      totalTrades: acc.totalTrades + monthData.totalTrades,
      tradeDays: acc.tradeDays + monthData.tradeDays,
      pnl: acc.pnl + monthData.pnl,
      totalBuy: acc.totalBuy,
      wins: acc.wins + monthData.wins,
      losses: acc.losses + monthData.losses,
      totalPayout: acc.totalPayout + monthData.totalPayout,
      totalReinvest: acc.totalReinvest + monthData.totalReinvest,
      totalPort: acc.totalPort + monthData.totalPort,
    }), { totalTrades: 0, tradeDays: 0, pnl: 0, totalBuy: 0, wins: 0, losses: 0, totalPayout: 0, totalReinvest: 0, totalPort: 0 });

    // Calculate total buy for the year
    let yearTotalBuy = 0;
    yearlyData.forEach(monthData => {
      for (let day = 1; day <= getMonthDays(year, monthData.month); day++) {
        const dateKey = `${year}-${String(monthData.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const dayTrades = tradesByDate[dateKey] || [];
        dayTrades.forEach(trade => {
          yearTotalBuy += Number(trade.total_buy_price) || 0;
        });
      }
    });

    const pnlPercent = yearTotalBuy > 0 ? ((summary.pnl / yearTotalBuy) * 100).toFixed(2) : null;
    const totalTradingDays = summary.wins + summary.losses;
    const winPercent = totalTradingDays > 0 ? ((summary.wins / totalTradingDays) * 100).toFixed(2) : "0.00";
    const lossPercent = totalTradingDays > 0 ? ((summary.losses / totalTradingDays) * 100).toFixed(2) : "0.00";

    return {
      ...summary,
      pnlPercent,
      winPercent,
      lossPercent,
    };
  }, [yearlyData, year, tradesByDate]);

  const handlePrevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const handlePrevYear = () => {
    setYear(year - 1);
  };

  const handleNextYear = () => {
    setYear(year + 1);
  };

  const handleDayClick = (dateKey) => {
    // Navigate to Trades with date filter
    navigate(`/trades?start=${dateKey}&end=${dateKey}`);
  };

  const handleMonthClick = (monthIndex) => {
    setMonth(monthIndex);
    setViewMode("monthly");
  };

  // Calendar grid: 0 = Sunday
  const firstDayOfWeek = getFirstDayOfWeek(year, month);
  const daysInMonth = getMonthDays(year, month);
  const weeks = [];
  let week = [];
  // Fill leading blanks
  for (let i = 0; i < firstDayOfWeek; i++) week.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    week.push(calendarData[day - 1]);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  // Fill trailing blanks
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  return (
    <div>
      {/* View Mode Selector */}
      <div className="mb-4 d-flex align-items-center gap-2">
        <button
          className={`btn ${viewMode === "monthly" ? "btn-primary" : "btn-outline-secondary"}`}
          onClick={() => setViewMode("monthly")}
        >
          Monthly
        </button>
        <button
          className={`btn ${viewMode === "yearly" ? "btn-primary" : "btn-outline-secondary"}`}
          onClick={() => setViewMode("yearly")}
        >
          Yearly
        </button>
      </div>

      {viewMode === "monthly" ? (
        <div>
      {/* Statistics Summary (Non-Dollar) */}
      <table className="table table-bordered mb-4 shadow-sm" style={{ maxWidth: 700, background: "#fff" }}>
        <tbody>
          <tr style={{ background: "#f8f9fa" }}>
            <th>Trades</th>
            <th>Trade Days</th>
            <th>P&amp;L %</th>
            <th>Win %</th>
            <th>Loss %</th>
          </tr>
          <tr>
            <td>{monthSummary.totalTrades}</td>
            <td>{monthSummary.tradeDays}</td>
            <td>
              <span className={monthSummary.pnl >= 0 ? "pnl-positive" : "pnl-negative"}>
                {monthSummary.pnlPercent !== null ? `${monthSummary.pnlPercent}%` : ""}
              </span>
            </td>
            <td>
              <span className="pnl-positive">{winLossSummary.winPercent}%</span>
            </td>
            <td>
              <span className="pnl-negative">{winLossSummary.lossPercent}%</span>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Dollar Amounts Summary */}
      <table className="table table-bordered mb-4 shadow-sm" style={{ maxWidth: 700, background: "#fff" }}>
        <tbody>
          <tr style={{ background: "#f8f9fa" }}>
            <th>Total P&amp;L</th>
            <th>Payout</th>
            <th>Re-invest</th>
            <th>Port</th>
          </tr>
          <tr>
            <td>
              <span className={monthSummary.pnl >= 0 ? "pnl-positive" : "pnl-negative"}>
                {formatCurrency(monthSummary.pnl)}
              </span>
            </td>
            <td>
              <span className={monthSummary.totalPayout >= 0 ? "pnl-positive" : "pnl-negative"}>
                {formatCurrency(monthSummary.totalPayout)}
              </span>
            </td>
            <td>
              <span className={monthSummary.totalReinvest >= 0 ? "pnl-positive" : "pnl-negative"}>
                {formatCurrency(monthSummary.totalReinvest)}
              </span>
            </td>
            <td>
              <span className={monthSummary.totalPort >= 0 ? "pnl-positive" : "pnl-negative"}>
                {formatCurrency(monthSummary.totalPort)}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="d-flex align-items-center mb-4">
        <button className="btn btn-outline-secondary btn-sm me-2" onClick={handlePrevMonth}>&lt;</button>
        <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: 1 }}>
          {year}/{String(month + 1).padStart(2, "0")}
        </span>
        <button className="btn btn-outline-secondary btn-sm ms-2" onClick={handleNextMonth}>&gt;</button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="table table-bordered calendar-table shadow-sm" style={{ tableLayout: "fixed", width: "100%", textAlign: "center", background: "#fff" }}>
          <thead>
            <tr style={{ background: "#f8f9fa" }}>
              <th style={{ width: "14%" }}>Sun</th>
              <th style={{ width: "14%" }}>Mon</th>
              <th style={{ width: "14%" }}>Tue</th>
              <th style={{ width: "14%" }}>Wed</th>
              <th style={{ width: "14%" }}>Thu</th>
              <th style={{ width: "14%" }}>Fri</th>
              <th style={{ width: "14%" }}>Sat</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, i) => (
              <tr key={i}>
                {week.map((day, j) => {
                  if (!day) return <td key={j} style={{ background: "#f9f9f9", height: 90 }} />;
                  let cellClass = "";
                  if (day.hasTrades) {
                    cellClass = day.pnl > 0 ? "pnl-positive" : (day.pnl < 0 ? "pnl-negative" : "");
                  }
                  return (
                    <td
                      key={j}
                      className={cellClass + " calendar-cell"}
                      style={{
                        cursor: day.hasTrades ? "pointer" : "default",
                        verticalAlign: "top",
                        padding: 6,
                        borderWidth: 2,
                        borderRadius: 8,
                        background: day.hasTrades ? undefined : "#f9f9f9",
                        minWidth: 110,
                        minHeight: 90,
                        transition: "background 0.2s",
                        boxShadow: day.hasTrades ? "0 1px 4px rgba(0,0,0,0.04)" : undefined,
                      }}
                      onClick={() => day.hasTrades && handleDayClick(day.date)}
                    >
                      <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 2 }}>{day.day}</div>
                      {day.hasTrades && day.pnl !== null && (
                        <div style={{ fontSize: 13, lineHeight: 1.3 }}>
                          <div>
                            <span style={{ fontWeight: 600 }}>P&amp;L: </span>
                            <span>{formatCurrency(day.pnl)}</span>
                          </div>
                          <div>
                            <span style={{ fontWeight: 600 }}>%</span>{" "}
                            <span>{day.pnlPercent !== null ? `${day.pnlPercent}%` : ""}</span>
                          </div>
                          <hr style={{ margin: "6px 0", borderColor: "#e0e0e0" }} />
                          <div>
                            <span style={{ fontWeight: 600 }}>Payout:</span>{" "}
                            <span>{formatCurrency(day.payout)}</span>
                          </div>
                          <div>
                            <span style={{ fontWeight: 600 }}>Re-invest:</span>{" "}
                            <span>{formatCurrency(day.reinvest)}</span>
                          </div>
                          <div>
                            <span style={{ fontWeight: 600 }}>Port:</span>{" "}
                            <span>{formatCurrency(day.port)}</span>
                          </div>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </div>
      ) : (
        <div>
          {/* Statistics Summary (Non-Dollar) */}
          <table className="table table-bordered mb-4 shadow-sm" style={{ maxWidth: 700, background: "#fff" }}>
            <tbody>
              <tr style={{ background: "#f8f9fa" }}>
                <th>Trades</th>
                <th>Trade Days</th>
                <th>P&amp;L %</th>
                <th>Win %</th>
                <th>Loss %</th>
              </tr>
              <tr>
                <td>{yearlySummary.totalTrades}</td>
                <td>{yearlySummary.tradeDays}</td>
                <td>
                  <span className={yearlySummary.pnl >= 0 ? "pnl-positive" : "pnl-negative"}>
                    {yearlySummary.pnlPercent !== null ? `${yearlySummary.pnlPercent}%` : ""}
                  </span>
                </td>
                <td>
                  <span className="pnl-positive">{yearlySummary.winPercent}%</span>
                </td>
                <td>
                  <span className="pnl-negative">{yearlySummary.lossPercent}%</span>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Dollar Amounts Summary */}
          <table className="table table-bordered mb-4 shadow-sm" style={{ maxWidth: 700, background: "#fff" }}>
            <tbody>
              <tr style={{ background: "#f8f9fa" }}>
                <th>Total P&amp;L</th>
                <th>Payout</th>
                <th>Re-invest</th>
                <th>Port</th>
              </tr>
              <tr>
                <td>
                  <span className={yearlySummary.pnl >= 0 ? "pnl-positive" : "pnl-negative"}>
                    {formatCurrency(yearlySummary.pnl)}
                  </span>
                </td>
                <td>
                  <span className={yearlySummary.totalPayout >= 0 ? "pnl-positive" : "pnl-negative"}>
                    {formatCurrency(yearlySummary.totalPayout)}
                  </span>
                </td>
                <td>
                  <span className={yearlySummary.totalReinvest >= 0 ? "pnl-positive" : "pnl-negative"}>
                    {formatCurrency(yearlySummary.totalReinvest)}
                  </span>
                </td>
                <td>
                  <span className={yearlySummary.totalPort >= 0 ? "pnl-positive" : "pnl-negative"}>
                    {formatCurrency(yearlySummary.totalPort)}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Year navigation */}
          <div className="d-flex align-items-center mb-4">
            <button className="btn btn-outline-secondary btn-sm me-2" onClick={handlePrevYear}>&lt;</button>
            <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: 1 }}>
              {year}
            </span>
            <button className="btn btn-outline-secondary btn-sm ms-2" onClick={handleNextYear}>&gt;</button>
          </div>

          {/* Monthly breakdown table */}
          <table className="table table-bordered shadow-sm" style={{ background: "#fff", maxWidth: 1000 }}>
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                <th>Month</th>
                <th>Trades</th>
                <th>Trade Days</th>
                <th>Total P&amp;L</th>
                <th>P&amp;L %</th>
                <th>Win %</th>
                <th>Loss %</th>
                <th>Payout</th>
                <th>Re-invest</th>
                <th>Port</th>
              </tr>
            </thead>
            <tbody>
              {yearlyData.map((monthData, idx) => {
                const rowClass = monthData.totalTrades === 0 ? "" : (monthData.pnl >= 0 ? "yearly-pnl-positive" : "yearly-pnl-negative");
                return (
                  <tr
                    key={idx}
                    className={rowClass}
                    onClick={() => handleMonthClick(monthData.month)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ fontWeight: 600 }}>{monthNames[monthData.month]}</td>
                    <td>{monthData.totalTrades}</td>
                    <td>{monthData.tradeDays}</td>
                    <td>
                      <span className={monthData.pnl >= 0 ? "pnl-positive" : "pnl-negative"}>
                        {formatCurrency(monthData.pnl)}
                      </span>
                    </td>
                    <td>
                      <span className={monthData.pnl >= 0 ? "pnl-positive" : "pnl-negative"}>
                        {monthData.pnlPercent !== null ? `${monthData.pnlPercent}%` : ""}
                      </span>
                    </td>
                    <td>
                      <span className="pnl-positive">{monthData.winPercent}%</span>
                    </td>
                    <td>
                      <span className="pnl-negative">{monthData.lossPercent}%</span>
                    </td>
                    <td>
                      <span className={monthData.totalPayout >= 0 ? "pnl-positive" : "pnl-negative"}>
                        {formatCurrency(monthData.totalPayout)}
                      </span>
                    </td>
                    <td>
                      <span className={monthData.totalReinvest >= 0 ? "pnl-positive" : "pnl-negative"}>
                        {formatCurrency(monthData.totalReinvest)}
                      </span>
                    </td>
                    <td>
                      <span className={monthData.totalPort >= 0 ? "pnl-positive" : "pnl-negative"}>
                        {formatCurrency(monthData.totalPort)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>
        {`
        .pnl-positive {
          color: #1a7f37;
        }
        .pnl-negative {
          color: #b91c1c;
        }
        .calendar-table td.pnl-positive {
          background: #e6ffe6 !important;
          color: #1a7f37;
          border-color: #b6eab6;
        }
        .calendar-table td.pnl-negative {
          background: #ffe6e6 !important;
          color: #b91c1c;
          border-color: #f5b6b6;
        }
        .calendar-table td.calendar-cell:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.10);
          z-index: 2;
        }
        .calendar-table td {
          min-height: 70px;
          font-size: 15px;
          border-radius: 8px;
          transition: box-shadow 0.2s;
        }
        .calendar-table {
          border-radius: 10px;
          overflow: hidden;
        }
        .yearly-pnl-positive {
          background: #e6ffe6 !important;
        }
        .yearly-pnl-negative {
          background: #ffe6e6 !important;
        }
        tbody tr[style*="cursor: pointer"]:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          transform: translateY(-1px);
          transition: all 0.2s;
        }
        `}
      </style>
    </div>
  );
};

export default Dashboard;
